/* eslint-disable @typescript-eslint/no-var-requires */
/**
 * Vendor-agnostic product analytics façade (PostHog v1 adapter).
 * Fail-open: never throw; synchronous capture so logout ordering is deterministic.
 */

import CryptoJS from "crypto-js";

const ALLOWED_EVENTS = /** @type {const} */ ([
    "user_login",
    "user_logout",
    "page_view",
    "feature_used",
    "import_schedule",
    "publish_schedule",
    "dukc_plans_update",
    "external_navigation",
    "sso_started",
    "sso_completed",
]);

/**
 * Temporarily disabled events: still part of the v1 catalog above (kept intentionally,
 * not removed) but suppressed at the façade so they are never sent to PostHog for now.
 * Active events: `user_login`, `page_view`, `import_schedule`, `publish_schedule`.
 * To re-enable an event, remove it from this set.
 * @type {Set<string>}
 */
const DISABLED_EVENTS = new Set([
    "user_logout",
    "feature_used",
    "dukc_plans_update",
    "external_navigation",
    "sso_started",
    "sso_completed",
]);

/** @param {string} event */
function isEventEnabled(event) {
    return ALLOWED_EVENTS.includes(/** @type {any} */ (event)) && !DISABLED_EVENTS.has(event);
}

const PAGE_NAMES = /** @type {const} */ ([
    "timeline_view",
    "max_draft_view",
    "grid_view",
    "planned_view",
    "movement_details",
]);

/** @typedef {typeof PAGE_NAMES[number]} PageName */

/** @type {{ user_id: string | null; org_id: string | null; role: string | null }} */
let globalContext = {
    user_id: null,
    org_id: null,
    role: null,
};

function isAnalyticsEnabled() {
    if (typeof window === "undefined") return false;
    const toggle = typeof process !== "undefined" ? process.env.NEXT_PUBLIC_ANALYTICS_ENABLED : "";
    if (toggle !== "true") return false;
    const key = typeof process !== "undefined" ? process.env.NEXT_PUBLIC_POSTHOG_KEY : "";
    return Boolean(key && String(key).trim());
}

/** @returns {string} */
function getIdPepper() {
    return (
        (typeof process !== "undefined" && process.env.NEXT_PUBLIC_ANALYTICS_ID_PEPPER) ||
        "omc_portal_analytics_pepper_rotate_in_production"
    );
}

/** @param {string} cognitoSub */
export function hashCognitoSub(cognitoSub) {
    try {
        return CryptoJS.SHA256(`${getIdPepper()}:${cognitoSub}`).toString(CryptoJS.enc.Hex);
    } catch {
        return "";
    }
}

/**
 * Base64-decode JWT payload (no signature verification — client analytics only).
 * @param {string} jwt
 */
export function decodeJwtPayloadUnsafe(jwt) {
    if (!jwt || typeof jwt !== "string" || jwt.split(".").length < 2) return null;
    try {
        const payload = jwt.split(".")[1];
        const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
        const decoded =
            typeof window !== "undefined" && typeof window.atob === "function"
                ? decodeURIComponent(
                      Array.prototype.map
                          .call(window.atob(normalized), (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                          .join(""),
                  )
                : Buffer.from(normalized, "base64").toString("utf8");
        return JSON.parse(decoded);
    } catch {
        return null;
    }
}

/**
 * Identity from Cognito id_token / bearer JWT claims.
 * @param {Record<string, unknown>} payload
 */
function contextFromJwtPayload(payload) {
    if (!payload || typeof payload !== "object") return null;
    const sub = payload.sub;
    if (typeof sub !== "string" || !sub) return null;

    const orgRaw = payload["custom:org_id"] ?? payload.org_id ?? payload["custom:tenant_id"] ?? "";
    const org_id = typeof orgRaw === "string" ? orgRaw : String(orgRaw || "");

    let role = "";
    const groups = payload["cognito:groups"];
    if (Array.isArray(groups)) {
        role = groups.filter((g) => typeof g === "string").join(",");
    } else if (typeof groups === "string") {
        role = groups;
    } else if (payload["custom:role"] != null) {
        role = String(payload["custom:role"]);
    }

    const rawEmail = payload.email;
    const email = typeof rawEmail === "string" && rawEmail.includes("@") ? rawEmail : null;

    const jwtName = typeof payload.name === "string" ? payload.name.trim() : "";

    const user_id = hashCognitoSub(sub);
    return { user_id, org_id: org_id || null, role: role || null, email, name: jwtName || null };
}

/**
 * Read JWT from rails session storage (`rails_token.data.jwt`).
 */
export function tryGetRailsJwtFromStorage() {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem("rails_token");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const jwt = parsed?.data?.jwt;
        return typeof jwt === "string" && jwt ? jwt : null;
    } catch {
        return null;
    }
}

/** @returns {{ name?: string; email?: string }} */
function tryReadRailsTokenProfile() {
    /** @type {{ name?: string; email?: string }} */
    const out = {};
    if (typeof window === "undefined") return out;
    try {
        const raw = window.localStorage.getItem("rails_token");
        if (!raw) return out;
        const parsed = JSON.parse(raw);
        const d = parsed?.data;
        if (!d || typeof d !== "object") return out;
        if (typeof d.name === "string" && d.name.trim()) out.name = d.name.trim();
        if (typeof d.email === "string" && d.email.includes("@")) out.email = d.email.trim();
    } catch {
        /* */
    }
    return out;
}

/** Pick person properties for PostHog (`identify` second arg — not duplicated on raw event payloads). */
function pickPersonProperties(traits) {
    if (!traits || typeof traits !== "object") return {};
    /** @type {Record<string, unknown>} */
    const out = {};
    if (traits.email != null && String(traits.email).trim()) out.email = String(traits.email).trim();
    if (traits.name != null && String(traits.name).trim()) out.name = String(traits.name).trim();
    if (traits.org_id != null && String(traits.org_id).trim()) out.org_id = String(traits.org_id).trim();
    if (traits.role != null && String(traits.role).trim()) out.role = String(traits.role).trim();
    return out;
}

/** @returns {typeof import("./posthogClient") | null} */
function getAdapterSync() {
    try {
        // eslint-disable-next-line import/no-extraneous-dependencies
        return require("./posthogClient");
    } catch {
        return null;
    }
}

function snakeCaseKeys(obj) {
    if (!obj || typeof obj !== "object") return {};
    /** @type {Record<string, unknown>} */
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
        if (!/^[a-z][a-z0-9_]*$/.test(k)) continue;
        out[k] = v;
    }
    return out;
}

function mergedProperties(extra) {
    const base = {
        ...(globalContext.user_id != null && globalContext.user_id !== "" ? { user_id: globalContext.user_id } : {}),
        ...(globalContext.org_id != null && globalContext.org_id !== ""
            ? { org_id: globalContext.org_id }
            : {}),
        ...(globalContext.role != null && globalContext.role !== "" ? { role: globalContext.role } : {}),
    };
    return { ...base, ...snakeCaseKeys(extra || {}) };
}

function adapterRegisterGlobals() {
    if (!isAnalyticsEnabled()) return;
    try {
        const ph = getAdapterSync();
        if (!ph) return;
        ph.initPostHogClient();
        ph.registerPostHogSuperProps({
            ...(globalContext.user_id ? { user_id: globalContext.user_id } : {}),
            ...(globalContext.org_id ? { org_id: globalContext.org_id } : {}),
            ...(globalContext.role ? { role: globalContext.role } : {}),
        });
    } catch {
        /* fail-open */
    }
}

function adapterCapture(event, properties, options) {
    if (!isAnalyticsEnabled()) return;
    try {
        const ph = getAdapterSync();
        if (!ph) return;
        ph.initPostHogClient();
        ph.capturePostHogEvent(event, properties, options);
    } catch {
        /* fail-open */
    }
}

function adapterIdentify(distinctId, traits) {
    if (!isAnalyticsEnabled() || !distinctId) return;
    try {
        const ph = getAdapterSync();
        if (!ph) return;
        ph.initPostHogClient();
        ph.identifyPostHogUser(distinctId, traits || {});
    } catch {
        /* fail-open */
    }
}

function adapterReset() {
    try {
        const ph = getAdapterSync();
        if (!ph) return;
        ph.resetPostHogIdentity();
    } catch {
        /* fail-open */
    }
}

function setSentryAnalyticsContext() {
    if (typeof window === "undefined") return;
    try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const Sentry = require("@sentry/nextjs");
        if (globalContext.user_id) {
            Sentry.setUser({ id: globalContext.user_id });
        } else {
            Sentry.setUser(null);
        }
        if (globalContext.org_id) {
            Sentry.setTag("org_id", globalContext.org_id);
        } else {
            Sentry.setTag("org_id", "");
        }
    } catch {
        /* Sentry optional / dev */
    }
}

function clearSentryAnalyticsContext() {
    if (typeof window === "undefined") return;
    try {
        const Sentry = require("@sentry/nextjs");
        Sentry.setUser(null);
        Sentry.setTag("org_id", "");
    } catch {
        /* */
    }
}

/** @type {{ key: string; at: number }} */
let lastPageViewDedupe = { key: "", at: 0 };

/**
 * Last identity signature actually sent to the adapter. Prevents re-emitting
 * PostHog's `$identify` / `$set` (person properties) events on every route change
 * when the user and their traits have not changed.
 * @type {string}
 */
let lastIdentifyKey = "";

export const analytics = {
    /**
     * @param {string} event
     * @param {Record<string, unknown>} [properties]
     * @param {{ sendInstantly?: boolean }} [options]
     */
    track(event, properties, options) {
        try {
            if (!isAnalyticsEnabled()) return;
            if (!event || typeof event !== "string" || !/^[a-z][a-z0-9_]*$/.test(event)) return;
            if (!isEventEnabled(event)) return;
            // Ensure PostHog identify runs before capture so events aren't sent anonymously
            // when page components fire before _app.js finishes its async identity sync.
            if (!globalContext.user_id) {
                this.syncIdentityFromRailsStorage();
            }
            adapterCapture(event, mergedProperties(properties), options);
        } catch {
            /* */
        }
    },

    /**
     * @param {string} userId Hashed Cognito user id
     * @param {{ org_id?: string | null; role?: string | null; email?: string | null; name?: string | null }} [traits]
     */
    identify(userId, traits) {
        try {
            if (!isAnalyticsEnabled()) return;

            /** @type {string | undefined} */
            const userIdSafe = typeof userId === "string" ? userId : userId === null ? undefined : String(userId ?? "");
            if (!userIdSafe || userIdSafe === "undefined") return;

            globalContext = {
                user_id: userIdSafe,
                org_id: traits?.org_id != null && traits.org_id !== "" ? String(traits.org_id) : null,
                role: traits?.role != null && traits.role !== "" ? String(traits.role) : null,
            };

            const personProps = pickPersonProperties({
                ...traits,
                org_id: globalContext.org_id ?? undefined,
                role: globalContext.role ?? undefined,
            });

            const identifyKey = `${globalContext.user_id}|${JSON.stringify(personProps)}`;
            if (identifyKey !== lastIdentifyKey) {
                adapterIdentify(globalContext.user_id, personProps);
                lastIdentifyKey = identifyKey;
            }
            adapterRegisterGlobals();
            setSentryAnalyticsContext();
        } catch {
            /* */
        }
    },

    /** Shallow snapshot of hashed event-level identity (`user_id`, `org_id`, `role`) — no PII. */
    getIdentity() {
        return { ...globalContext };
    },

    /**
     * Identify using Cognito JWT plus optional backend `name`/`email`; merges `rails_token` profile fields.
     * @param {{ jwt?: string | null; name?: string | null; email?: string | null }} [args]
     */
    identifyWithBackendUser({ jwt, name, email } = {}) {
        try {
            if (!isAnalyticsEnabled()) return;

            /** @type {string | undefined} */
            const jwtSafe = jwt && typeof jwt === "string" ? jwt.trim() : undefined;
            if (!jwtSafe) return;

            /** @type {Record<string, unknown>} */
            let payload = {};
            try {
                payload = decodeJwtPayloadUnsafe(jwtSafe) ?? {};
            } catch {
                return;
            }
            const ctx = contextFromJwtPayload(payload);
            if (!ctx?.user_id) return;

            const fromStorage = tryReadRailsTokenProfile();

            /** @type {string | undefined} */
            let resolvedEmail;
            if (email != null && String(email).trim() && String(email).includes("@")) {
                resolvedEmail = String(email).trim();
            } else if (fromStorage.email) {
                resolvedEmail = fromStorage.email;
            } else if (ctx.email) {
                resolvedEmail = ctx.email;
            }

            /** @type {string | undefined} */
            let resolvedName;
            if (name != null && String(name).trim()) {
                resolvedName = String(name).trim();
            } else if (fromStorage.name) {
                resolvedName = fromStorage.name;
            } else if (ctx.name) {
                resolvedName = ctx.name;
            }

            this.identify(ctx.user_id, {
                org_id: ctx.org_id ?? undefined,
                role: ctx.role ?? undefined,
                ...(resolvedEmail ? { email: resolvedEmail } : {}),
                ...(resolvedName ? { name: resolvedName } : {}),
            });
        } catch {
            /* */
        }
    },

    /**
     * Decode JWT, update global context, identify + Sentry.
     * @param {string} jwt
     */
    identifyFromCognitoJwtString(jwt) {
        this.identifyWithBackendUser({ jwt });
    },

    /** Sync identity from `rails_token` JWT if present. */
    syncIdentityFromRailsStorage() {
        try {
            const jwt = tryGetRailsJwtFromStorage();
            if (jwt) this.identifyWithBackendUser({ jwt });
        } catch {
            /* */
        }
    },

    reset() {
        try {
            globalContext = { user_id: null, org_id: null, role: null };
            lastIdentifyKey = "";
            clearSentryAnalyticsContext();
            adapterReset();
        } catch {
            /* */
        }
    },

    /** Call on explicit user logout — emits `user_logout` (when enabled) before clearing identity */
    logout() {
        try {
            if (isEventEnabled("user_logout")) {
                adapterCapture("user_logout", mergedProperties({}));
            }
            globalContext = { user_id: null, org_id: null, role: null };
            lastIdentifyKey = "";
            clearSentryAnalyticsContext();
            adapterReset();
        } catch {
            /* */
        }
    },

    /**
     * @param {PageName} pageName
     */
    trackPageView(pageName) {
        if (!PAGE_NAMES.includes(/** @type {any} */ (pageName))) return;
        try {
            if (typeof window !== "undefined") {
                const fp = `${pageName}|${window.location.pathname}|${window.location.hash || ""}`;
                const now = Date.now();
                if (lastPageViewDedupe.key === fp && now - lastPageViewDedupe.at < 750) return;
                lastPageViewDedupe = { key: fp, at: now };
            }
            this.track("page_view", { page_name: pageName });
        } catch {
            /* */
        }
    },

    trackImportSchedule() {
        this.track("import_schedule");
    },

    trackPublishSchedule() {
        this.track("publish_schedule");
    },

    trackDukcPlansUpdate() {
        this.track("dukc_plans_update");
    },

    /**
     * Legacy alias — maps old feature keys to dedicated PostHog event names.
     * @param {"schedule_import_tidalis" | "schedule_download" | "dukc_plans_update"} featureName
     */
    trackFeatureUsed(featureName) {
        const eventByFeature = {
            schedule_import_tidalis: "import_schedule",
            schedule_download: "publish_schedule",
            dukc_plans_update: "dukc_plans_update",
        };
        const event = eventByFeature[featureName];
        if (!event) return;
        this.track(event);
    },

    /**
     * @param {string} targetProduct snake_case product key
     */
    trackExternalNavigation(targetProduct) {
        if (!targetProduct || typeof targetProduct !== "string" || !/^[a-z][a-z0-9_]*$/.test(targetProduct)) return;
        this.track("external_navigation", { target_product: targetProduct }, { sendInstantly: true });
    },
};

export { PAGE_NAMES };
